import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { Card, CardBody } from '../../components/Card';
import { TextInput } from '../../components/Field';
import { CrossSiteNotice } from '../../components/CrossSiteNotice';
import { errorMessage } from '../../lib/api';
import { useAuthStore } from '../../app/authStore';

/**
 * 登录页。
 *
 * 不做自助注册：账号由管理员创建。因此这里没有「注册」入口，
 * 只在页脚说明如何拿到账号。
 */
export function LoginPage() {
  const status = useAuthStore((state) => state.status);
  const error = useAuthStore((state) => state.error);
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await login(email.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (loginError) {
      setFormError(errorMessage(loginError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-0 p-6 text-fg">
      <div className="w-full max-w-sm">
        <div className="mb-5 flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-md bg-accent text-base font-bold text-accent-fg">H</span>
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold">Havorix 员工平台</span>
            <span className="text-[11px] text-fg-subtle">提出创意 · AI 快速研判 · 协作推进</span>
          </div>
        </div>

        <Card>
          <CardBody>
            <form onSubmit={onSubmit} className="flex flex-col gap-3">
              <CrossSiteNotice />
              <TextInput
                label="邮箱"
                type="email"
                autoComplete="username"
                placeholder="name@havorix.cn"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <TextInput
                label="口令"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              {formError ?? error ? <p className="text-xs text-critical">{formError ?? error}</p> : null}
              <Button type="submit" variant="primary" loading={submitting} block>
                登录
              </Button>
            </form>
          </CardBody>
        </Card>

        <p className="mt-3 text-[11px] leading-relaxed text-fg-subtle">
          平台不开放自助注册：账号由管理员创建，初始口令属于共享秘密，首次登录会被要求立即修改。
          忘记口令请联系管理员重置。
        </p>
      </div>
    </div>
  );
}
